import { useEffect, useState } from "react";
import { Datepicker } from "@mobiscroll/react";
import { NavDropdown } from "react-bootstrap";
import { Link } from "react-router-dom";
import { FilterCriteria } from "tasks-experience-client";
import { IcdCode, WorkflowState } from "common-util-experience-client";
import { WorkflowConstants, WorkflowStateConstants } from "../../helper/WorkflowConstants";
import { GridWorkflowStatusDropdown } from "../../grid/GridWorkflowStatusDropdown";
import { GridUsersDropdown } from "../../grid/GridUsersDropdown";
import { UsersDto } from "users-experience-client";
import { createCommonUtilsIcdApi, createWorkflowApi } from "../../api-helpers/CommonUtilsApiHelper";
import { GridDropdown } from "../../grid/GridDropdown";
import { workflowStatesCache } from "../../grid/renderers/WorkflowStateCellValueGetter";

interface CarePlanHeaderProps {
  patientId?: string;
  currentUserId: string;
  onUpdateFilters: (filterArray: FilterCriteria[]) => void;
  breadcrumbs: boolean | undefined;
}

const getCurrentMonthRange = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
  const end = new Date(now.getFullYear(), now.getMonth() + 1, now.getDate());
  return [start, end];
};
const defaultDateRange = getCurrentMonthRange();

export const CarePlanHeader = (props: CarePlanHeaderProps) => {

  const icdApi = createCommonUtilsIcdApi()
  const workflowApi = createWorkflowApi();

  const [searchText, setSearchText] = useState<string>();
  const [createdOnDateRange, setCreatedOnDateRange] = useState<[Date, Date] | []>([]);
  const [reviewedOnDateRange, setReviewedOnDateRange] = useState<[Date, Date] | []>([]);
  const [reviewedBy, setReviewedBy] = useState<string[] | null>();
  const [statuses, setStatuses] = useState<WorkflowState[] | null>();
  const [conditions, setConditions] = useState<IcdCode[] | null>();

  const getConditionsMaster = async () => {
    try {
      const conditionsResponse = await icdApi.icdCodesControllerGetAllIcdCodes() 
      return conditionsResponse.data;
    } catch (error) {
      console.log(error);
      return [] as IcdCode[];
    }
  };

  const filters: FilterCriteria[] = [];
  const updateFilters = async () => {

    if(!props.patientId) {
      const carePlanStatuses = await workflowApi.workflowControllerGetStatesForWorkflow(WorkflowConstants.carePlan);

      const reviewPendingStatusId = carePlanStatuses.data.find((data)=> data.stateName===WorkflowStateConstants.ReviewPending)?.stateId;
      if(reviewPendingStatusId){
        filters.push({
          propertyName: "designerStateId",
          operator: "equals",
          propertyValue: reviewPendingStatusId
        });
      } 
    }

    if (props.patientId) {
      filters.push({
        propertyName: "patientId",
        operator: "equals",
        propertyValue: props.patientId
      });
      if (reviewedOnDateRange && reviewedOnDateRange.length > 0) {
        filters.push({
          propertyName: "reviewedAt",
          operator: "greater-than-equals",
          propertyValue: reviewedOnDateRange[0]
        });
        filters.push({
          propertyName: "reviewedAt",
          operator: "less-than-equals",
          propertyValue: reviewedOnDateRange[1]
        });
      }
      if (reviewedBy && reviewedBy.length > 0) {
        filters.push({
          propertyName: "reviewedBy",
          operator: "in",
          propertyValue: reviewedBy
        });
      }
      if (statuses && statuses.length > 0) {
        filters.push({
          propertyName: "designerStateId",
          operator: "in",
          propertyValue: statuses.map(p => p.stateId)
        });
      }
    }
    if ((searchText?.length ?? 0) > 0) {
      filters.push({
        propertyName: "conditionsNames",
        operator: "contains",
        propertyValue: searchText
      });
    }
    if(conditions && conditions.length > 0) {
      filters.push({
        propertyName: "conditionsNames",
        operator: "contains-in",
        propertyValue: conditions.map(condition => condition.code + ": " + condition.shortDescription)
      });
    }
    if (createdOnDateRange && createdOnDateRange.length > 1) {
      filters.push({
        propertyName: "createdAt",
        operator: "greater-than-equals",
        propertyValue: createdOnDateRange[0],
      });
      filters.push({
        propertyName: "createdAt",
        operator: "less-than-equals",
        propertyValue: createdOnDateRange[1],
      });
    }

    props.onUpdateFilters(filters);
  }

  useEffect(()=>{updateFilters()}, [searchText, createdOnDateRange, reviewedOnDateRange, reviewedBy, statuses, conditions]);

  useEffect(() => {
    getConditionsMaster()
  }, [])

  const handleCreatedOnDateChange = (event: any) => {
    const [start, end] = event.value;
    if (!start || !end) {
      return;
    }

    // Set start date to local 00:00:00
    const startDate = new Date(start);
    startDate.setHours(0, 0, 0, 0);
  
    // Set end date to local 23:59:59
    const endDate = new Date(end);
    endDate.setHours(23, 59, 59, 999);

    setCreatedOnDateRange([startDate, endDate]);
  };
  const handleReviewedOnDateChange = (event: any) => {
    const [start, end] = event.value;

    const startDate = new Date(start);
    startDate.setHours(0, 0, 0, 0);

    let endDate;
    if (!end) {
        endDate = new Date(startDate);
        endDate.setMonth(endDate.getMonth() + 1);
    } else {
        endDate = new Date(end);
    }
    // Set end date to local 23:59:59
    endDate.setHours(23, 59, 59, 999);
    
    setReviewedOnDateRange([startDate, endDate]);
  };

  const updateAssignedTo = (values: UsersDto[]) => {
    setReviewedBy(values ? values.map(v => v.id) : null);
  }

  return (
    <>
      { props.breadcrumbs && <div
          className="d-flex align-items-center px-3 px-md-4 px-lg-5 py-2 border-bottom"
          aria-label="breadcrumb"
        >
          <div className="flex-fill d-flex align-items-center">
            <div>
              <ol className="breadcrumb">
                <li className="breadcrumb-item breadcrumb-item-first">
                  <Link to="/home">
                    <span className="material-symbols-outlined fs-14">
                      home
                    </span>
                  </Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Care Plans
                </li>
              </ol>
              <h1 className="h3 m-0">Care Plans</h1>
            </div>
          </div>
        </div> }

      <div
        className="d-md-flex align-items-center px-3 px-md-4 px-lg-5 py-2 border-bottom"
      >
        <div className="flex-fill d-flex align-items-center">
          <div className="search-box max-w-md-18-75">
            <span className="material-symbols-outlined search-box__icon">
              search
            </span>
            <div className="typeahead-container search-box d-flex">
              <span className="material-symbols-outlined search-box__icon">
                search
              </span>
              <input
                type="text"
                name="searchCarePlan"
                className="form-control search-box__input fw-normal fs-12"
                placeholder="Search Care Plan"
                onChange={(event) => setSearchText(event.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="d-flex flex-wrap align-items-center">
          {props.patientId ? 
            <GridWorkflowStatusDropdown
              label="Status"
              type={WorkflowConstants.carePlan}
              onChange={setStatuses}
            /> : null
          }
          <GridDropdown
            label="Conditions"
            keyProvider={(icdCode) => icdCode?.id}
            labelProvider={(icdCode) => icdCode.code + " : " + icdCode.shortDescription}
            dataProvider={getConditionsMaster}
            onChange={setConditions}
          />
          <NavDropdown
            id="nav-dropdown-dark-example"
            title="Created On"
            className={`btn-dropdown ms-md-2 me-2 me-md-0 mt-2 mt-md-0 ${createdOnDateRange.length ? 'border border-primary rounded-2' : 'border border-white'}`}
          >
            <div style={{ width: "500px" }}>
              <Datepicker
                controls={["calendar"]}
                select="range"
                calendarType="month"
                pages={2}
                touchUi={true}
                display="inline"
                value={createdOnDateRange}
                onChange={handleCreatedOnDateChange}
                theme="windows"
                themeVariant="light"
              />
            </div>
          </NavDropdown>

          {props.patientId ? <>
            <NavDropdown
              id="nav-dropdown-dark-example"
              title="Reviewed On"
              className={`btn-dropdown ms-md-2 me-2 me-md-0 mt-2 mt-md-0 ${reviewedOnDateRange.length ? 'border border-primary rounded-2' : 'border border-white'}`}
            >
              <div style={{ width: "500px" }}>
                <Datepicker
                  controls={["calendar"]}
                  select="range"
                  calendarType="month"
                  pages={2}
                  touchUi={true}
                  display="inline"
                  value={reviewedOnDateRange}
                  onChange={handleReviewedOnDateChange}
                  theme="windows"
                  themeVariant="light"
                />
              </div>
            </NavDropdown>

            <GridUsersDropdown
              label="Reviewed By"
              onChange={updateAssignedTo}
              initialUserIds={reviewedBy ?? []}
            />
          </> : null}
        </div>
      </div>
    </>
  );
};
